import { createSignal, createMemo, createEffect, on } from 'solid-js';
import { createContext, consume, type ICustomElement } from 'component-register';
import debounce from 'lodash.debounce';
import { getCollapsibleItemContext } from '@brytdesigns/web-component-collapsible';

import { fadeInBatch, fadeOutBatch, prefersReducedMotion } from './faq-search-animations';
import { highlight } from './faq-search-text';

export { formatTemplate, highlight } from './faq-search-text';

export interface ItemRecord {
  groupHandle: string;
  questionText: string;
  answerText: string;
  questionEl: HTMLElement | null;
  answerEl: HTMLElement | null;
  originalQuestionHTML: string;
  originalAnswerHTML: string;
  collapsible: HTMLElement | null;
  autoExpanded: boolean;
}

export interface ContextProps {
  query: string;
  activeCategory: string;
  delay: number;
}

export interface ContextState extends ContextProps {
  element: HTMLElement & ICustomElement;
}

export interface FaqStateAPI {
  query: () => string;
  lowerQuery: () => string;
  activeCategory: () => string;
  visibleByGroup: () => Map<string, number>;
  visibleTotal: () => number;
  hasInteracted: () => boolean;
}

export interface FaqActions {
  setQuery: (q: string) => void;
  flushQuery: (q: string) => void;
  setActiveCategory: (handle: string) => void;
  registerItem: (element: HTMLElement, info: ItemRecord) => void;
  registerGroup: (element: HTMLElement, handle: string) => void;
  registerEmpty: (element: HTMLElement) => void;
}

export type FaqContextValue = [FaqStateAPI, FaqActions];

export const FaqContext = createContext((state: ContextState): FaqContextValue => {
  const [items, setItems] = createSignal<Map<HTMLElement, ItemRecord>>(new Map());
  const [groups, setGroups] = createSignal<Map<HTMLElement, string>>(new Map());
  let emptyEl: HTMLElement | null = null;
  let hasInteracted = false;

  const lowerQuery = createMemo(() => state.query.trim().toLowerCase());

  const visibleByGroup = createMemo<Map<string, number>>(() => {
    const q = lowerQuery();
    const ac = state.activeCategory;
    const counts = new Map<string, number>();
    for (const info of items().values()) {
      const inCat = ac === 'all' || info.groupHandle === ac;
      const matches = !q || info.questionText.includes(q) || info.answerText.includes(q);
      if (inCat && matches) counts.set(info.groupHandle, (counts.get(info.groupHandle) ?? 0) + 1);
    }
    return counts;
  });

  const visibleTotal = createMemo(() => {
    let sum = 0;
    for (const c of visibleByGroup().values()) sum += c;
    return sum;
  });

  createEffect(
    on(
      () => ({ q: lowerQuery(), ac: state.activeCategory, itemsMap: items(), groupsMap: groups() }),
      ({ q, ac, itemsMap, groupsMap }, prev) => {
        const hasQ = q !== '';
        const counts = visibleByGroup();
        const toShow: HTMLElement[] = [];
        const toHide: HTMLElement[] = [];
        const groupStable = new Map<string, boolean>();

        for (const [groupEl, handle] of groupsMap) {
          const wasShown = groupEl.style.display !== 'none';
          const willShow = (ac === 'all' || ac === handle) && (!hasQ || (counts.get(handle) ?? 0) > 0);
          groupStable.set(handle, wasShown && willShow);
          groupEl.style.display = willShow ? '' : 'none';
        }

        for (const [el, info] of itemsMap) {
          const inCategory = ac === 'all' || ac === info.groupHandle;
          const matches = !q || info.questionText.includes(q) || info.answerText.includes(q);
          const matchInAnswer = !!q && !info.questionText.includes(q) && info.answerText.includes(q);
          const visible = inCategory && matches;

          if (info.questionEl) info.questionEl.innerHTML = info.originalQuestionHTML;
          if (info.answerEl) info.answerEl.innerHTML = info.originalAnswerHTML;
          if (visible && q) {
            if (info.questionEl) highlight(info.questionEl, q);
            if (info.answerEl) highlight(info.answerEl, q);
          }

          const wasVisible = el.style.display !== 'none';
          if (visible !== wasVisible) {
            if (groupStable.get(info.groupHandle)) (visible ? toShow : toHide).push(el);
            else el.style.display = visible ? '' : 'none';
          }

          if (info.collapsible) {
            const ctx = getCollapsibleItemContext(info.collapsible);
            if (ctx) {
              const [, { expand, collapse }] = ctx;
              if (visible && matchInAnswer && !info.autoExpanded) {
                expand();
                info.autoExpanded = true;
              } else if (info.autoExpanded && (!visible || !matchInAnswer)) {
                collapse();
                info.autoExpanded = false;
              }
            }
          }
        }

        const empty = emptyEl;
        if (empty) {
          const isEmpty = hasQ && visibleTotal() === 0;
          const emptyWasVisible = empty.style.display !== 'none';
          if (isEmpty && !emptyWasVisible) toShow.push(empty);
          else if (!isEmpty && emptyWasVisible) {
            empty.getAnimations().forEach((a) => a.cancel());
            empty.style.display = 'none';
            empty.style.height = '';
            empty.style.overflow = '';
          }
        }

        const shouldAnimate = prev && hasInteracted && !prefersReducedMotion();
        if (shouldAnimate) {
          fadeOutBatch(toHide).then(() => fadeInBatch(toShow));
        } else {
          toShow.forEach((el) => (el.style.display = ''));
          toHide.forEach((el) => (el.style.display = 'none'));
        }
      },
    ),
  );

  const debouncedSetQuery = debounce((q: string) => {
    state.element.setAttribute('query', q);
  }, state.delay);

  return [
    {
      query: () => state.query,
      lowerQuery,
      activeCategory: () => state.activeCategory,
      visibleByGroup,
      visibleTotal,
      hasInteracted: () => hasInteracted,
    },
    {
      setQuery: (q) => {
        hasInteracted = true;
        debouncedSetQuery(q);
      },
      flushQuery: (q) => {
        hasInteracted = true;
        debouncedSetQuery.cancel();
        state.element.setAttribute('query', q);
      },
      setActiveCategory: (handle) => {
        hasInteracted = true;
        state.element.setAttribute('active-category', handle);
        const url = new URL(window.location.href);
        if (handle === 'all') url.searchParams.delete('category');
        else url.searchParams.set('category', handle);
        if (url.toString() !== window.location.href) window.history.pushState({}, '', url.toString());
      },
      registerItem: (el, info) => setItems((m) => (m.has(el) ? m : new Map(m).set(el, info))),
      registerGroup: (el, handle) => setGroups((m) => (m.has(el) ? m : new Map(m).set(el, handle))),
      registerEmpty: (el) => {
        emptyEl = el;
        el.removeAttribute('hidden');
        el.style.display = 'none';
      },
    },
  ];
});

export const useFaq = (element: HTMLElement & ICustomElement): FaqContextValue => {
  const ctx = consume(FaqContext, element);
  if (!ctx) throw new Error('faq-search: must be inside <faq-context>');
  return ctx;
};
